import { FunctionalityEnum, UserRoleEnum } from '@elearning-platform/api-types/enums';
import { getPathWithParams, IconName, SideMenuItem, StickyMenuItem } from '@elearning-platform/ui';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useCallback, useMemo } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { Location, useLocation } from 'react-router';
import Cookies from 'universal-cookie';

import { phpApi } from '../api';
import { fetchCertificatePaths } from '../api/requests';
import { AppConfig } from '../appConfig';
import { menuPositions } from '../consts';
import { QueryKey, RouteNameEnum } from '../enums';
import { CertificatePath, CertificatePathsList, LangRouteInfo, MenuPosition } from '../interfaces';
import { UseAuth, UseMenuItems, UseRedirectTo } from '../interfaces/hooks';
import { getRouteNamesByUrl, getUrlByRouteName } from '../utils';

import { useAuth } from './useAuth.hook';
import { useRedirectTo } from './useRedirectTo.hook';

export const useMenuItems: () => UseMenuItems = (): UseMenuItems => {
  const { t }: TransProps<never> = useTranslation();
  const { redirect }: UseRedirectTo = useRedirectTo();
  const { pathname }: Location = useLocation();
  const { appSettings, hasUserRequiredFunctionalities, isAuthInitialized, isRouteAvailableForMenuRole, userData }: UseAuth = useAuth();

  const hasAccessToCertificatePathsList: boolean = useMemo(
    (): boolean => !!isAuthInitialized && hasUserRequiredFunctionalities([FunctionalityEnum.ApiCertificateCertificatePathList]),
    [isAuthInitialized, hasUserRequiredFunctionalities]
  );

  const { data: certificatePathsData }: UseQueryResult<AxiosResponse<CertificatePathsList>> = useQuery({
    queryKey: [QueryKey.CertificatePaths],
    enabled: hasAccessToCertificatePathsList,
    queryFn: (): Promise<AxiosResponse<CertificatePathsList>> => fetchCertificatePaths() as Promise<AxiosResponse<CertificatePathsList>>,
  });

  const ofwcaCertificatePath: CertificatePath | undefined = useMemo(
    (): CertificatePath | undefined => [
      ...certificatePathsData?.data?.finished ?? [],
      ...certificatePathsData?.data?.inProgress ?? [],
    ].find((item: CertificatePath): boolean => item.type === 'ofwca'),
    [certificatePathsData]
  );

  const handleRedirect: () => void = useCallback((): void => {
    void phpApi.post('/api/user/management', {}).then(({ data }: AxiosResponse): void => {
      const cookies: Cookies = new Cookies();

      Object.keys(data.cookies).forEach((element: string): void => {
        cookies.set(element, data.cookies[element], { path: '/' });
      });

      redirect({ externalUrl: `${AppConfig.api}${data.redirect}`, isExternalUrl: true, inNewTab: true });
    });
  }, [redirect]);

  const replaceParamsFromApi: (pathname: RouteNameEnum) => string = useCallback((pathname: RouteNameEnum): string => {
    const ofwcaCertificatePathBaseUrl: string | undefined = getUrlByRouteName(pathname)?.url;
    if (pathname === RouteNameEnum.OfwcaCertificatePath && ofwcaCertificatePath && ofwcaCertificatePathBaseUrl) {
      return getPathWithParams(ofwcaCertificatePathBaseUrl, { id: ofwcaCertificatePath.id.toString() });
    }

    return '';
  }, [ofwcaCertificatePath]);

  const isPublicRoute: (routeObject: LangRouteInfo) => boolean = (routeObject: LangRouteInfo): boolean => (
    !routeObject.requiredFunctionalities
    || ('operator' in routeObject.requiredFunctionalities && !routeObject.requiredFunctionalities.functionalities.length)
    || (Array.isArray(routeObject.requiredFunctionalities) && !routeObject.requiredFunctionalities.length)
  );

  const mainPositions: SideMenuItem[] = useMemo(
    (): SideMenuItem[] => [
      ...menuPositions
        .filter((item: MenuPosition): boolean => {
          const routeObject: LangRouteInfo | undefined = getUrlByRouteName(item.pathname);

          if (!routeObject || isPublicRoute(routeObject)) {
            return true;
          }

          return (
            isAuthInitialized
            && !!appSettings?.currentMenuRole
            && hasUserRequiredFunctionalities(routeObject.requiredFunctionalities)
            && isRouteAvailableForMenuRole(item.pathname, appSettings.currentMenuRole)
            && (
              item.pathname !== RouteNameEnum.OfwcaCertificatePath
              || !!userData?.availableRoutes.api_ofwca_dashboard
            )
          );
        })
        .map((item: MenuPosition): SideMenuItem => {
          const routeObject: LangRouteInfo | undefined = getUrlByRouteName(item.pathname);

          return {
            name: item.pathname,
            label: t(item.label),
            internalPath: !routeObject?.url.includes(':')
              ? routeObject?.url
              : replaceParamsFromApi(item.pathname),
            icon: item.icon,
          };
        }),
      ...(
        hasUserRequiredFunctionalities([FunctionalityEnum.ManagementActionList])
        && appSettings?.currentMenuRole !== UserRoleEnum.USER
      )
        ? [{
            name: 'management',
            label: t('app.mainNavigation.management'),
            icon: IconName.PageManagement,
            action: handleRedirect,
          }]
        : [],
    ],
    [
      t,
      isAuthInitialized,
      appSettings?.currentMenuRole,
      handleRedirect,
      hasUserRequiredFunctionalities,
      replaceParamsFromApi,
      isRouteAvailableForMenuRole,
      userData?.availableRoutes,
    ]
  );

  const stickyMobileMenuPositions: StickyMenuItem[] = useMemo(
    (): StickyMenuItem[] => menuPositions
      .filter((item: MenuPosition): boolean => {
        const routeObject: LangRouteInfo | undefined = getUrlByRouteName(item.pathname);

        if (!item.renderOnStickyMobileMenu) {
          return false;
        }
        if (!routeObject || isPublicRoute(routeObject)) {
          return true;
        }

        return isAuthInitialized && hasUserRequiredFunctionalities(routeObject.requiredFunctionalities);
      })
      .map((item: MenuPosition): StickyMenuItem => {
        const routeObject: LangRouteInfo | undefined = getUrlByRouteName(item.pathname);

        return {
          name: item.pathname,
          path: !routeObject?.url.includes(':')
            ? routeObject?.url ?? '/'
            : replaceParamsFromApi(item.pathname),
          icon: item.icon,
        };
      }),
    [isAuthInitialized, hasUserRequiredFunctionalities, replaceParamsFromApi]
  );

  const getPostionNameByCustomLogic: (currentRouteNames: RouteNameEnum[]) => RouteNameEnum | undefined = useCallback((
    currentRouteNames: RouteNameEnum[]
  ): RouteNameEnum | undefined => {
    const ofwcaCertificatePathBaseUrl: string | undefined = getUrlByRouteName(RouteNameEnum.OfwcaCertificatePath)?.url;
    if (
      currentRouteNames.includes(RouteNameEnum.OfwcaCertificatePath)
      && ofwcaCertificatePathBaseUrl
      && pathname === getPathWithParams(
        ofwcaCertificatePathBaseUrl,
        { id: ofwcaCertificatePath?.id?.toString() ?? '' }
      )
    ) {
      return RouteNameEnum.OfwcaCertificatePath;
    }
  }, [pathname, ofwcaCertificatePath]);

  const currentMenuPositionName: RouteNameEnum | undefined = useMemo(
    (): RouteNameEnum | undefined => {
      const currentRouteNames: RouteNameEnum[] = getRouteNamesByUrl(pathname);
      if (!currentRouteNames.length) {
        return undefined;
      }
      const postionNameByCustomLogic: RouteNameEnum | undefined = getPostionNameByCustomLogic(currentRouteNames);
      if (postionNameByCustomLogic) {
        return postionNameByCustomLogic;
      }

      return mainPositions.find((item: SideMenuItem): boolean => {
        const positionUrl: string | undefined = getUrlByRouteName(item.name as RouteNameEnum)?.url;

        return !!positionUrl && !!getUrlByRouteName(currentRouteNames[0])?.url.startsWith(positionUrl);
      })?.name as RouteNameEnum;
    },
    [mainPositions, pathname, getPostionNameByCustomLogic]
  );

  return {
    currentMenuPositionName,
    mainPositions,
    stickyMobileMenuPositions,
  };
};
